const STORAGE_PREFIX = 'scout';
const CURRENT_USER_META_NAME = 'current-user-id';

interface VersionedStorageEntry<T> {
  expiresAt?: number;
  value: T;
  version: number;
}

interface VersionedStorageOptions<T> {
  legacyKeys?: string[];
  scope?: 'browser' | 'user';
  ttlMs?: number;
  userId?: string;
  validate?: (value: unknown) => value is T;
  version: number;
}

export const currentUserId = (): string => {
  if (typeof document === 'undefined') return 'anonymous';

  return (
    document.querySelector<HTMLMetaElement>(`meta[name="${CURRENT_USER_META_NAME}"]`)?.content ??
    'anonymous'
  );
};

export const versionedUserCookieName = (key: string, version: number): string =>
  `${key}_user_${currentUserId()}_v${version}`;

export const synchronizeSessionStorageUser = (version: number): void => {
  try {
    const ownerKey = `${STORAGE_PREFIX}:sessionOwner:v${version}`;
    const userId = currentUserId();
    if (sessionStorage.getItem(ownerKey) !== userId) {
      sessionStorage.clear();
      sessionStorage.setItem(ownerKey, userId);
    }
  } catch {
    // Session storage can be unavailable. Existing page state remains optional.
  }
};

const storageKey = (
  key: string,
  {
    scope = 'user',
    version,
    userId
  }: Pick<VersionedStorageOptions<unknown>, 'scope' | 'version' | 'userId'>
): string => {
  const namespace = scope === 'user' ? `user:${userId ?? currentUserId()}` : 'browser';
  return `${STORAGE_PREFIX}:${namespace}:${key}:v${version}`;
};

const removeLegacyKeys = (legacyKeys: string[] = []): void => {
  legacyKeys.forEach((key) => localStorage.removeItem(key));
};

const removeOtherVersions = (key: string, options: VersionedStorageOptions<unknown>): void => {
  const currentKey = storageKey(key, options);
  const versionPrefix = currentKey.replace(/v\d+$/, 'v');

  Object.keys(localStorage).forEach((storedKey) => {
    if (storedKey.startsWith(versionPrefix) && storedKey !== currentKey) {
      localStorage.removeItem(storedKey);
    }
  });
};

export const readVersionedLocalStorage = <T>(
  key: string,
  options: VersionedStorageOptions<T>
): T | null => {
  const keyForVersion = storageKey(key, options);

  try {
    removeLegacyKeys(options.legacyKeys);
    removeOtherVersions(key, options);
    const raw = localStorage.getItem(keyForVersion);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<VersionedStorageEntry<unknown>>;
    const isExpired = parsed.expiresAt !== undefined && parsed.expiresAt <= Date.now();
    const isInvalid = options.validate ? !options.validate(parsed.value) : false;

    if (parsed.version !== options.version || isExpired || isInvalid) {
      localStorage.removeItem(keyForVersion);
      return null;
    }

    return parsed.value as T;
  } catch {
    localStorage.removeItem(keyForVersion);
    return null;
  }
};

export const writeVersionedLocalStorage = <T>(
  key: string,
  value: T,
  options: VersionedStorageOptions<T>
): void => {
  try {
    removeLegacyKeys(options.legacyKeys);
    removeOtherVersions(key, options);
    const entry: VersionedStorageEntry<T> = {
      value,
      version: options.version,
      ...(options.ttlMs ? { expiresAt: Date.now() + options.ttlMs } : {})
    };
    localStorage.setItem(storageKey(key, options), JSON.stringify(entry));
  } catch {
    // Storage can be unavailable or full. Callers continue without persistence.
  }
};

export const removeVersionedLocalStorage = (
  key: string,
  options: Pick<VersionedStorageOptions<unknown>, 'legacyKeys' | 'scope' | 'version'>
): void => {
  try {
    localStorage.removeItem(storageKey(key, options));
    removeLegacyKeys(options.legacyKeys);
  } catch {
    // Ignore unavailable storage.
  }
};

export const removeVersionedLocalStorageByPrefix = (
  keyPrefix: string,
  scope: 'browser' | 'user' = 'user'
): void => {
  try {
    const namespace = scope === 'user' ? `user:${currentUserId()}` : 'browser';
    const prefix = `${STORAGE_PREFIX}:${namespace}:${keyPrefix}`;
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(prefix)) localStorage.removeItem(key);
    });
  } catch {
    // Ignore unavailable storage.
  }
};
